import { useEffect, useState } from "react";

import { ValueSetExpansionContains } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { NativeSelect, Stack, Textarea, TextInput } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";

import {
  ClearableYesNoRadioField,
  isOtherDisplaySelected,
  yesNoOtherRadioOptions,
} from "@/components/ClearableYesNoRadioField";
import {
  CLIENT_IN_WATER_LINK_ID,
  CLIENT_IN_WATER_OTHER_LINK_ID,
  CLIENT_POSITION_LINK_ID,
  POSITION_DATE_LINK_ID,
  POSITION_DATE_TIME_LINK_ID,
  POSITION_NOTES_LINK_ID,
  POSITION_TIME_LINK_ID,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/position/constants/position-link-ids";
import type { PositionFlowsheetDrawerMode } from "@/encounters/components/EncounterLabor/flowsheet-forms/position/types/position-drawer";
import type { PositionFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/position/types/position-form";
import { valueSetContainsToNativeSelectOptions } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-coding-select";
import { LABOR_FLOWSHEET_DRAWER_MODES } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-drawer-constants";
import { LaborFlowsheetDateTimeFields } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetDateTimeFields";
import { LaborFlowsheetSignatureInitialsField } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetSignatureInitialsField";
import { QuiltedHealthValueSets } from "@/fhir/enums/quilted-health";
import { questionnaireFieldTestId } from "@/intake-forms/utils/questionnaire-field-testid";
import { getValueSetOptions } from "@/utils/medplum";
import { toast } from "@/utils/notifications";

interface PositionFormFieldsProps {
  form: UseFormReturnType<PositionFlowsheetFormValues>;
  clientId: string;
  mode?: PositionFlowsheetDrawerMode;
  isDrawerOpened?: boolean;
}

export function PositionFormFields({
  form,
  clientId,
  mode = LABOR_FLOWSHEET_DRAWER_MODES.QuickAdd,
  isDrawerOpened = true,
}: PositionFormFieldsProps) {
  const medplum = useMedplum();
  const [clientPositionOptions, setClientPositionOptions] = useState<ValueSetExpansionContains[]>(
    [],
  );
  const showClientInWaterOther = isOtherDisplaySelected(form.values.clientInWater);
  const isQuickAddMode = mode === LABOR_FLOWSHEET_DRAWER_MODES.QuickAdd;
  const clientPositionInputProps = form.getInputProps(CLIENT_POSITION_LINK_ID);

  useEffect(() => {
    if (!isDrawerOpened) {
      return;
    }

    let isCancelled = false;

    const loadClientPositionOptions = async () => {
      try {
        const clientPositionValueSet = await getValueSetOptions(
          medplum,
          QuiltedHealthValueSets.LaborPosition,
        );
        if (isCancelled) {
          return;
        }
        setClientPositionOptions(clientPositionValueSet ?? []);
      } catch (error) {
        console.error("Error loading Position client position value set:", error);
        if (!isCancelled) {
          toast({
            type: "error",
            title: "Failed to load form options",
            message:
              "Client position options could not be loaded. Try closing and reopening the drawer.",
            autoClose: false,
          });
        }
      }
    };

    void loadClientPositionOptions();

    return () => {
      isCancelled = true;
    };
  }, [isDrawerOpened, medplum]);

  return (
    <Stack gap="lg">
      <LaborFlowsheetDateTimeFields
        form={form}
        dateFieldName={POSITION_DATE_LINK_ID}
        timeFieldName={POSITION_TIME_LINK_ID}
        autoPopulateDateTime={isQuickAddMode}
        dateTimeGroupLinkId={POSITION_DATE_TIME_LINK_ID}
      />

      <NativeSelect
        label="Client's Position"
        data-path={CLIENT_POSITION_LINK_ID}
        data={valueSetContainsToNativeSelectOptions(clientPositionOptions)}
        {...clientPositionInputProps}
        value={clientPositionInputProps.value ?? ""}
        data-testid={questionnaireFieldTestId(CLIENT_POSITION_LINK_ID)}
      />

      <ClearableYesNoRadioField
        form={form}
        label="Client in Water"
        name={CLIENT_IN_WATER_LINK_ID}
        options={yesNoOtherRadioOptions}
        dataPath={CLIENT_IN_WATER_LINK_ID}
        dataTestId={questionnaireFieldTestId(CLIENT_IN_WATER_LINK_ID)}
        onClear={() => form.setFieldValue(CLIENT_IN_WATER_OTHER_LINK_ID, "")}
      />

      {showClientInWaterOther ? (
        <TextInput
          label="Other"
          placeholder="Please Specify Other"
          data-path={CLIENT_IN_WATER_OTHER_LINK_ID}
          {...form.getInputProps(CLIENT_IN_WATER_OTHER_LINK_ID)}
          data-testid={questionnaireFieldTestId(CLIENT_IN_WATER_OTHER_LINK_ID)}
        />
      ) : null}

      <Textarea
        label="Notes"
        data-path={POSITION_NOTES_LINK_ID}
        {...form.getInputProps(POSITION_NOTES_LINK_ID)}
        data-testid={questionnaireFieldTestId(POSITION_NOTES_LINK_ID)}
      />

      <LaborFlowsheetSignatureInitialsField
        form={form}
        clientId={clientId}
        label="Sign Initials"
        name="signatureInitials"
        autoPopulateSignature={isQuickAddMode}
      />
    </Stack>
  );
}
